/* eslint-disable @typescript-eslint/no-empty-object-type */
import { IMappedProps, MapProps } from '../interfaces/mappedProps'
import { TextProps } from '../ui/components/Text'
import { getAllValues, IGetValueResult } from './getValue'
import { deepMerge } from './deepMerge'
import { isNullish, isString } from './validations'
import { evalConditional } from './conditionals'
import { sortByKey } from './sort'
import { toCamelCase } from './toCamelCase'

/**
 * Validate group of conditional props and return the first group that meets all of them and merge with the default props
 * @param value
 * @param map
 */
const getConditionalProps = <Map>(value: unknown, map?: MapProps<Map>) => {
  const { conditionalProps = [], ...rest } = map || {}
  const result = conditionalProps.find((conditionalGroup) => {
    const { conditionals = [] } = conditionalGroup
    return conditionals.every((conditional) => {
      const { condition, value: conditionValue } = conditional
      return evalConditional(value, condition, conditionValue)
    })
  })

  if (result) {
    const { ...newProps } = result || {}
    return deepMerge(rest, newProps) as typeof map
  }
  return undefined
}

/**
 * Get the assigned map to an item
 * @param value
 * @param key
 * @param maps
 */
const getItemMap = <Map>(
  valueResult: IGetValueResult,
  maps: Record<string, MapProps<Map>>
) => {
  const { key, value } = valueResult
  const valueMap = maps?.[String(value)] || maps?.[toCamelCase(String(value))]
  const keyMap = maps?.[key]
  let itemMap = valueMap || keyMap
  itemMap = getConditionalProps(value, itemMap) || itemMap

  const { type = 'tag' } = itemMap || {}
  if (itemMap && type === 'tag') {
    const { props: { label = String(value), ...newProps } = {} } = itemMap
    const newLabel = (isString(label) ? { value: label } : label) as TextProps
    newLabel.value = newLabel.value?.replaceAll('{{value}}', String(value))

    itemMap = {
      ...itemMap,
      props: {
        ...newProps,
        label: newLabel
      }
    }
  }
  return itemMap ? ({ key, value, ...itemMap } as MapProps<Map>) : itemMap
}

/**
 * Evaluate list of items
 * @param itemData
 * @param mappedProps
 */
export const getMappedValues = <Map>(
  itemData: unknown = {},
  mappedProps: IMappedProps<Map> = {}
) => {
  const { items = [] } = mappedProps
  return getAllValues(items, itemData)
}

/**
 * Receives a list of values and merge them with mapped props, then returns only the values to be showed
 * @param values
 * @param mappedProps
 */
export const processMappedProps = <Map>(
  values: IGetValueResult[] = [],
  mappedProps: IMappedProps<Map> = {}
) => {
  const { map = {} } = mappedProps
  let items = values?.map((value) => {
    return getItemMap(value, map)
  })
  // remove unused items
  items = items.filter((item, index) => {
    const { show = true, value, noRepeat } = item || {}
    const canRepeat =
      !noRepeat ||
      !items.some((item2, index2) => item2?.key === item?.key && index2 < index)
    return !!item && show && !isNullish(value) && canRepeat
  })
  // reorder by priority
  items = sortByKey(items, 'priority')

  return items
}
